import { useEffect } from 'react';
import {
  getRewrittenUrl,
  LEGACY_METRICS_KEY,
} from 'src/commons/utils/urlRewriteRules';
import { logger } from 'src/commons/utils/logger';
import useIsFeatureEnabled from 'src/commons/hooks/useIsFeatureEnabled';
import { WeblabedFeatures } from 'src/commons/constants';

/**
 * Intercepts navigation link clicks and rewrites legacy URLs to their
 * DSP equivalents when enableLegacyConversionMetrics is active in
 * session storage.
 *
 * This hook is primarily responsible for catching anchor links in the global
 * nav, pivot nav, and header. It does not catch programmatic navigation
 * (e.g. the region switcher).
 */
export default function useLegacyConversionMetricsUrlRewriter(): void {
  const isScopingNavEnabled = useIsFeatureEnabled(
    WeblabedFeatures.ADPT_SCOPING_NAV_DSP_OVERVIEW_ROLLOVER,
  );
  const isAndonCordEnabled = useIsFeatureEnabled(
    WeblabedFeatures.LEGACY_CONVERSION_METRICS_URL_REWRITE_ANDON_CORD,
  );

  useEffect(() => {
    // If the scoping nav weblab is not launched or if the andon cord has been
    // pulled, return without doing anything.
    if (!isScopingNavEnabled || isAndonCordEnabled) {
      return;
    }

    // If the param is explicitly set in the current URL but is missing in
    // session storage, set it in session storage. This lets us carry the state
    // across sessions when switching regions.
    try {
      const url = new URL(window.location.href);
      if (
        url.searchParams.get(LEGACY_METRICS_KEY) === 'true' &&
        window.sessionStorage.getItem(LEGACY_METRICS_KEY) !== 'true'
      ) {
        window.sessionStorage.setItem(LEGACY_METRICS_KEY, 'true');
        logger.info(
          'useLegacyConversionMetricsUrlRewriter: persisted enableLegacyConversionMetrics to session storage from URL param',
        );
      }
    } catch (e) {
      logger.warn(
        'useLegacyConversionMetricsUrlRewriter: failed to sync session storage from URL',
        e instanceof Error ? e : new Error(String(e)),
      );
    }

    // If legacy metrics are not enabled, we can skip registering the click
    // event listener.
    try {
      if (window.sessionStorage.getItem(LEGACY_METRICS_KEY) !== 'true') {
        logger.info(
          'useLegacyConversionMetricsUrlRewriter: legacy metrics not enabled, skipping click listener registration',
        );
        return;
      }
    } catch (e) {
      logger.warn(
        'useLegacyConversionMetricsUrlRewriter: sessionStorage unavailable, feature cannot activate',
        e instanceof Error ? e : new Error(String(e)),
      );
      return;
    }

    logger.info(
      'useLegacyConversionMetricsUrlRewriter: legacy metrics enabled, registering url rewriter',
    );

    const handleClick = (event: MouseEvent): void => {
      try {
        // Only process left-click (0) and middle-click (1). Right-click (2)
        // opens a context menu and should not mutate the anchor's href.
        if (event.button !== 0 && event.button !== 1) return;

        const target = event.target;
        if (!(target instanceof Element)) return;

        const anchor = target.closest('a');
        if (!anchor?.href) return;

        const originalHref = anchor.href;
        const rewrittenUrl = getRewrittenUrl(
          originalHref,
          window.location.href,
        );
        if (!rewrittenUrl) return;

        // Mutate the anchor's href so the browser's native default action
        // navigates to the rewritten URL. This preserves native behavior for
        // modifier keys (Ctrl/Meta → new tab, Shift → new window, etc.)
        // without reimplementing browser logic.
        //
        // This approach relies on downstream click handlers not calling
        // preventDefault or performing their own programmatic navigation for
        // the rewritten links. If a downstream handler reads the navigation
        // URL from React props (rather than the DOM href) and navigates
        // programmatically, the href mutation would have no effect.
        //
        // For example, AccordionContent's onClick handler would override this,
        // but is currently safe because it only performs client-side routing
        // for links with isCSREnabled=true, and the links matching our rewrite
        // rules have isCSREnabled=false — so it returns early and lets the
        // browser navigate using the mutated href.
        anchor.href = rewrittenUrl;
        logger.info(
          `useLegacyConversionMetricsUrlRewriter: rewrote URL from ${originalHref} to ${rewrittenUrl}`,
        );
      } catch (e) {
        // Never crash the page — this listener fires on every click.
        logger.warn(
          'useLegacyConversionMetricsUrlRewriter: handleClick error',
          e instanceof Error ? e : new Error(String(e)),
        );
      }
    };

    // Capture phase ensures this handler runs before any other click handlers,
    // so the href is rewritten before other listeners can act on the event.
    // auxclick handles middle-click (button=1) which fires auxclick, not click.
    document.addEventListener('click', handleClick, true);
    document.addEventListener('auxclick', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('auxclick', handleClick, true);
    };
  }, [isScopingNavEnabled, isAndonCordEnabled]);
}
